import axios from "axios";
import { API_BASE_URL } from "../../config";
import { authHeaders } from "../utils/session";

/**
 * The account-side half of the 360° view.
 *
 * The existing `user360Service` reads trademark intelligence from the search
 * cluster; these endpoints answer a different question — what this customer has
 * bought, spent, delegated and done inside the product. Different service,
 * different base URL, same screen.
 *
 * Several of the tables behind these are new. Where one has not been created in
 * an environment yet the API answers with an empty list rather than an error, so
 * a panel reads as "nothing here" and fills itself in the day the table lands.
 */

const get = async (path, params = {}) => {
  const clean = { ...params };
  Object.keys(clean).forEach((k) => {
    if (clean[k] === "" || clean[k] === undefined || clean[k] === null) delete clean[k];
  });
  const res = await axios.get(`${API_BASE_URL}/customer-360/${path}`, {
    params: clean,
    headers: authHeaders(),
  });
  return res.data || {};
};

/** Stat cards on Overview: wallet, AI credits, TM counts, team, tasks, last login. */
export const fetchCustomerSummary = (userId) => get("summary", { user_id: userId });

/** Wallet ledger, plus current/added/used totals alongside the rows. */
export const fetchWallet = (userId, page = 1, limit = 20) =>
  get("wallet", { user_id: userId, page, limit });

/** AI credit ledger — what was bought, what each call consumed. */
export const fetchAiCredits = (userId, page = 1, limit = 20) =>
  get("ai-credits", { user_id: userId, page, limit });

/** Raw API call log behind those credits: model, tokens, latency, failures. */
export const fetchAiUsage = (userId, page = 1, limit = 20) =>
  get("ai-usage", { user_id: userId, page, limit });

/** Notice replies drafted for this account, and how far each one got. */
export const fetchNoticeReplies = (userId, page = 1, limit = 20) =>
  get("notice-replies", { user_id: userId, page, limit });

/** Roles, who reports to whom, and the people holding them. Not paginated. */
export const fetchTeam = (userId) => get("team", { user_id: userId });

export const fetchTasks = (userId, page = 1, limit = 20) =>
  get("tasks", { user_id: userId, page, limit });

/** Customer-side login sessions — not CMS logins, which live elsewhere. */
export const fetchLogins = (userId, page = 1, limit = 20) =>
  get("logins", { user_id: userId, page, limit });

/**
 * Subscribed vs manually added trademarks, and which sub-user may manage what.
 */
export const fetchTmBreakdown = (userId, page = 1, limit = 20) =>
  get("tm-breakdown", { user_id: userId, page, limit });
